import dayjs from "dayjs"
import { Analytics, logEvent } from "firebase/analytics"
import XLSX from "xlsx"
import { AmountType } from "../Enum"
import {
  Buyer,
  ExcelBuyer,
  ExcelInventory,
  ExcelInvoice,
  ExcelRecord,
  Inventory,
  Invoice,
  Record,
  RecordType,
} from "../Type"
import { getRecordType } from "../Utils"
import { formatCurrencyAmount } from "./CurrencyUtils"
import { formatDate } from "./DateUtils"

/**
 * Converts Records to ExcelRecords by removing unnecessary info (e.g., recId, recTypeId)
 * before printing in Excel sheet
 */
const convertRecordsToExcelRecords = (
  records: Record[],
  recordTypes: RecordType[]
): ExcelRecord[] => {
  const excelRecords: ExcelRecord[] = []
  for (let rec of records) {
    excelRecords.push({
      Date: formatDate(rec.date),
      Amount: formatCurrencyAmount(rec.amount),
      Category: rec.credit ? AmountType.Credit : AmountType.Debit,
      Type: getRecordType(rec, recordTypes).name,
      Note: rec.note,
    })
  }
  return excelRecords
}

export const exportRecordsAsExcel = (
  records: Record[],
  recordTypes: RecordType[]
) => {
  /* make the worksheet */
  var ws = XLSX.utils.json_to_sheet(
    convertRecordsToExcelRecords(records, recordTypes)
  )

  /* add to workbook */
  var wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, "CreditDebit")

  var fileName = dayjs().format("DD-MM-YYYY") + "_CreditDebit.xlsx"

  /* generate an XLSX file */
  XLSX.writeFile(wb, fileName) // Does not work on iPhone and Android browsers
  //ga('send', 'event', 'Excel', 'export', fileName);
  //console.log('Excel is ready for export');
  // ga("send", "event", "excel", "export", "success")
}

/**
 * Converts Inventories to Excel Inventories by removing unnecessary info (e.g., firebaseId)
 * before printing in Excel sheet
 */
const convertInventoriesToExcelInventories = (
  inventories: Inventory[]
): ExcelInventory[] => {
  const excelInventories: ExcelInventory[] = []
  for (let item of inventories) {
    excelInventories.push({
      Name: item.name,
      UnitPrice_BDT: formatCurrencyAmount(item.unitPrice),
      Quantity: item.quantity,
    })
  }
  return excelInventories
}

export const exportInventoriesAsExcel = (
  inventories: Inventory[],
  analytics: Analytics
) => {
  /* make the worksheet */
  var ws = XLSX.utils.json_to_sheet(
    convertInventoriesToExcelInventories(inventories)
  )

  /* add to workbook */
  var wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, "Inventories")

  var fileName = dayjs().format("DD-MM-YYYY") + "_InventoryList.xlsx"

  /* generate an XLSX file */
  XLSX.writeFile(wb, fileName)
  //ga('send', 'event', 'Excel', 'export', fileName);
  //console.log('Excel is ready for export');
  logEvent(analytics, "web_excel_inventories_exported")
}

export const exportBuyersAsExcel = (buyers: Buyer[], analytics: Analytics) => {
  /* make the worksheet */
  var ws = XLSX.utils.json_to_sheet(convertBuyersToExcelBuyers(buyers))

  /* add to workbook */
  var wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, "Customers")

  var fileName = dayjs().format("DD-MM-YYYY") + "_CustomerList.xlsx"

  /* generate an XLSX file */
  XLSX.writeFile(wb, fileName)
  //ga('send', 'event', 'Excel', 'export', fileName);
  //console.log('Excel is ready for export');
  logEvent(analytics, "web_excel_buyers_exported")
}

/**
 * Converts Buyers to Excel Buyers by removing unnecessary info (e.g., firebaseId)
 * before printing in Excel sheet
 */
const convertBuyersToExcelBuyers = (buyers: Buyer[]): ExcelBuyer[] => {
  const excelBuyers: ExcelBuyer[] = []
  for (let item of buyers) {
    excelBuyers.push({
      Name: item.name,
      Address: item.address,
      Phone: item.phone,
      Email: item.email,
      Website: item.website,
    })
  }
  return excelBuyers
}

export const exportInvoicesAsExcel = (
  invoices: Invoice[],
  buyers: Buyer[],
  analytics: Analytics
) => {
  /* make the worksheet */
  var ws = XLSX.utils.json_to_sheet(
    convertInvoicesToExcelInvoices(invoices, buyers)
  )

  /* add to workbook */
  var wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, "Invoices")

  var fileName = dayjs().format("DD-MM-YYYY") + "_InvoiceList.xlsx"

  /* generate an XLSX file */
  XLSX.writeFile(wb, fileName)
  logEvent(analytics, "web_excel_invoices_exported")
}

/**
 * Converts Invoices to Excel Invoice by removing unnecessary info (e.g., firebaseId)
 * before printing in Excel sheet
 */
const convertInvoicesToExcelInvoices = (
  invoices: Invoice[],
  buyers: Buyer[]
): ExcelInvoice[] => {
  const excelInvoices: ExcelInvoice[] = []
  for (let item of invoices) {
    excelInvoices.push({
      Date: formatDate(dayjs(item.date).valueOf()),
      Customer: buyers.filter((buyer) => buyer.id === item.buyerId)[0]
        ? buyers.filter((buyer) => buyer.id === item.buyerId)[0].name
        : "",
      Items: item.totalItems,
      Price_BDT: formatCurrencyAmount(item.totalPrice),
    })
  }
  return excelInvoices
}
